import { useCallback, useEffect, useRef, useState } from 'react';
import type { ObjectProposalGenerationType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';

const useObjectDocumentGenerations = (objectId?: number | string) => {
    const [rows, setRows] = useState<ObjectProposalGenerationType[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [togglingId, setTogglingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const loadedObjectIdRef = useRef<number>(0);

    const refresh = useCallback(async () => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            setRows([]);
            loadedObjectIdRef.current = 0;
            setLoading(false);
            setRefreshing(false);
            return;
        }

        const isInitial = loadedObjectIdRef.current !== targetObjectId;
        if (isInitial) setLoading(true);
        else setRefreshing(true);

        const result = await apiDocument.getObjectDocumentGenerations(targetObjectId);
        setRows(result || []);
        loadedObjectIdRef.current = targetObjectId;
        setLoading(false);
        setRefreshing(false);
    }, [objectId]);

    const toggleAccept = useCallback(
        async (generationId: number, nextAccept: boolean): Promise<boolean> => {
            const targetObjectId = Number(objectId || 0);
            if (!targetObjectId || !generationId) return false;

            const target = rows.find(
                (row) => row.ID === generationId && (row.source || 'object') === 'object',
            );
            if (!target || Number(target.deleted || 0)) return false;

            setTogglingId(generationId);
            setRows((prev) =>
                prev.map((row) =>
                    row.ID === generationId && (row.source || 'object') === 'object'
                        ? { ...row, proposavAccept: nextAccept ? 1 : 0 }
                        : row,
                ),
            );

            const stat = await apiDocument.setObjectDocumentGenerationAccept(
                targetObjectId,
                generationId,
                nextAccept,
            );

            if (!stat) {
                setRows((prev) =>
                    prev.map((row) =>
                        row.ID === generationId && (row.source || 'object') === 'object'
                            ? { ...row, proposavAccept: nextAccept ? 0 : 1 }
                            : row,
                    ),
                );
            }

            setTogglingId(null);
            return Boolean(stat);
        },
        [objectId, rows],
    );

    const markDeleted = useCallback(
        async (generationId: number): Promise<boolean> => {
            const targetObjectId = Number(objectId || 0);
            if (!targetObjectId || !generationId) return false;

            const target = rows.find(
                (row) => row.ID === generationId && (row.source || 'object') === 'object',
            );
            if (!target || Number(target.deleted || 0)) return false;

            setDeletingId(generationId);
            const stat = await apiDocument.deleteObjectDocumentGeneration(targetObjectId, generationId);
            if (stat) {
                setRows((prev) =>
                    prev.map((row) =>
                        row.ID === generationId && (row.source || 'object') === 'object'
                            ? { ...row, deleted: 1 }
                            : row,
                    ),
                );
            }
            setDeletingId(null);
            return Boolean(stat);
        },
        [objectId, rows],
    );

    useEffect(() => {
        void refresh();
    }, [refresh]);

    return {
        rows,
        loading,
        refreshing,
        togglingId,
        deletingId,
        refresh,
        toggleAccept,
        markDeleted,
    };
};

export default useObjectDocumentGenerations;
